import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useNetworkStatus } from '@/presentation/hooks/useNetworkStatus';
import styles from './ConfigLoadingScreen.module.css';

const TIMEOUT_MS = 10_000;

export interface ConfigLoadingScreenProps {
  /** `null` enquanto carrega ou após sucesso. Presente quando a última
   * tentativa falhou por um motivo que não é sessão expirada (CA-07: nunca
   * quebra em tela branca — sempre mostra a mensagem + permite repetir). */
  errorMessage: string | null;
  onRetry: () => void;
}

/**
 * Tela de carregamento da configuração de ativação, exibida entre o PIN
 * validado e o restante do fluxo (HUB-235). Cobre os 3 estados de espera
 * previstos na spec: carregando, demorando mais que 10s (com "Tentar
 * novamente") e offline sem cache válido ("Aguardando conexão...").
 */
export function ConfigLoadingScreen({
  errorMessage,
  onRetry,
}: ConfigLoadingScreenProps): ReactElement {
  const online = useNetworkStatus();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (errorMessage) return undefined;

    setTimedOut(false);
    const timer = setTimeout(() => setTimedOut(true), TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  const showOfflineNotice = !online && !errorMessage;
  const showRetry = timedOut || errorMessage !== null;

  return (
    <main className={styles.container} aria-label="Carregando configuração" role="main">
      <div className={styles.content}>
        <div className={styles.spinnerArea} aria-hidden="true">
          <div className={styles.spinner} />
        </div>

        <h2 className={styles.heading}>Preparando ativação...</h2>
        <p className={styles.subtitle}>Sincronizando dados da agência</p>

        {showOfflineNotice && (
          <p className={styles.warning} role="status" aria-live="polite">
            Aguardando conexão...
          </p>
        )}

        {timedOut && !errorMessage && (
          <p className={styles.warning} role="status" aria-live="polite">
            Isso está demorando mais que o esperado.
          </p>
        )}

        {errorMessage && (
          <p className={styles.error} role="alert">
            {errorMessage}
          </p>
        )}

        {showRetry && (
          <button type="button" className={styles.retryButton} onClick={onRetry}>
            Tentar novamente
          </button>
        )}
      </div>
    </main>
  );
}
